import React, { useState, useEffect, useMemo, forwardRef } from 'react'
import { Checkbox, Input, Select, formatSelectOptions } from '../../../components'
import { getAllWebhookScopeDefs, getWebhookScopeLabel } from '../helpers'

const initialFormState = {
  scope: '',
  url: '',
  label: '',
  enabled: true,
}

// Add/edit form for a webhook registry row. The app scope is chosen on add and
// fixed on edit. The URL is masked: it is never sent to the SPA, so on edit the
// field starts blank with a "< hidden >" placeholder and a blank save keeps the
// stored URL intact (same convention as MAIL_PASSWORD).
const _AddWebhookForm = ({ mode, data, existingScopes = [], submitForm, loading, setLoading }, ref) => {

  const [formData, setFormData] = useState(initialFormState)

  // Options for the app dropdown: every webhook scope declared across apps that opt into
  // the 'webhook' App Config area (an allowlist, so a new app/sub-app declares its own
  // support — no hardcoded list here). On add, scopes that already have a webhook row
  // (existingScopes) are excluded — one webhook per scope, so an existing one is edited
  // rather than re-added. Computed lazily in the component (not at module load) to avoid
  // a circular import with the registry.
  const appOptions = useMemo(() => getAllWebhookScopeDefs()
    .filter(d => mode === 'edit' || !existingScopes.includes(d.scope))
    .map(d => ({ value: d.scope, label: getWebhookScopeLabel(d.scope) }))
    .sort((a, b) => a.label.localeCompare(b.label, 'sv')), [mode, existingScopes])

  useEffect(() => {
    if (mode === 'edit' && data && data.length === 1) {
      setFormData({
        scope: data[0].scope,
        url: '',
        label: data[0].label || '',
        enabled: data[0].enabled,
      })
    } else {
      setFormData(initialFormState)
    }
  }, [mode, data])

  const handleInputChange = (event) => {
    let name, value

    if (event.target !== undefined) {
      name = event.target.name
      value = event.target.type === 'checkbox' ? event.target.checked : event.target.value
    } else {
      name = event.name
      value = event.value
    }

    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const onSubmit = event => {
    event.preventDefault()

    if (!formData.scope) return

    setLoading(true)

    submitForm({
      scope: formData.scope,
      url: formData.url,
      label: formData.label,
      enabled: formData.enabled,
    })
    .catch(err => { throw err })
    .finally(() => setLoading(false))
  }

  const selectedAppLabel = (appOptions.find(o => o.value === formData.scope) || {}).label || formData.scope

  // When adding and every webhook-capable app already has a row, there is nothing left
  // to pick — show an informational placeholder instead of an empty, typeable dropdown.
  const noAppsAvailable = mode !== 'edit' && appOptions.length === 0

  return (
    <div className="form">
      <form onSubmit={onSubmit} ref={ref}>

        <div className="form-block vertical">
          <label>App</label>
          { mode === 'edit'
            ? <Input type="text" name="scope_display" value={selectedAppLabel} disabled={true} />
            : <Select
                name="scope"
                value={formData.scope}
                options={formatSelectOptions({ options: appOptions, optionValue: 'value', optionLabel: 'label' })}
                onChange={handleInputChange}
                disabled={loading || noAppsAvailable}
                placeholder={noAppsAvailable ? 'All apps are already configured' : '— Välj app —'}
              />
          }
        </div>

        <div className="form-block vertical">
          <Input type="password"
            name="url"
            label="Webhook-URL"
            value={formData.url}
            onChange={handleInputChange}
            disabled={loading}
            placeholder={mode === 'edit' ? '< hidden >' : ''}
            autoComplete='new-password'
          />
        </div>

        <div className="form-block vertical">
          <Input type="text"
            name="label"
            label="Etikett"
            value={formData.label}
            onChange={handleInputChange}
            disabled={loading}
            placeholder="t.ex. kanalnamn"
          />
        </div>

        <div className="form-block vertical">
          <div className="horizontal">
            <Checkbox
              name="enabled"
              checked={formData.enabled}
              onChange={handleInputChange}
              label="Aktiverad"
              disabled={loading}
            />
          </div>
        </div>

      </form>
    </div>
  )
}

export const AddWebhookForm = forwardRef(_AddWebhookForm)
